import { OrganisationConfiguration, Sex, SubscriptionCategory, SubscriptionType } from '@bciti/types';
import { Organisation } from '../../global.model';

export interface UserWallet {
    // TODO Review this interface when wallet/user data structure is refactored
    _id?: string;
    organisation?: string;
    user?: string;
    cards?: UserCardFE[];
}

export interface UserCard {
    _id: string;
    cardType: CardType;
    number: string;
    code: string;
    address: IAddress;
    expirationDate: string;
    subscriptionDate: string;
    status: CardStatus;
    lastName: string;
    firstName: string;
    photo: string;
    created: string;
    lastModified: string;
    proofOfIdentity: string[];
    renewal?: UserCard;
    linkable?: boolean;
    subType?: CardSubType;
    constraints?: CardContraints;
    actionType?: CardActionType;
    sex?: Sex;
    birthDate?: Date;
}

export interface CardContraints {
    displayInfo: boolean;
    message: string;
}

export interface UserCardAddress {
    city: string;
    direction: any;
    latitude: number;
    longitude: number;
    number: string;
    postCode: string;
    province: string;
    street: string;
    streetName: string;
    unit: string;
}

export interface LinkAddress {
    unit: string;
    direction: string;
    typeStreet: string;
    latitude: number;
    longitude: number;
    isPrimary: boolean;
    name: string;
    posdtCode: string;
    number: string;
    street: string;
    city: string;
    province: string;
    streetName: string;
}

export interface SubscribeCardPayload {
    cardType: string;
    address: UserCardAddress;
    lastName: string;
    firstName: string;
    birthDate: string;
    sex: string;
    photo: string;
    proofOfIdentity: string[];
}

export interface RenewCardPayload extends SubscribeCardPayload {
    _id: string;
}

export interface LinkCardPayload {
    cardType: string;
    cardNumber: string;
}

export enum CardType {
    BCITI = 'BCITI',
    LIBRARY = 'LIBRARY',
    SPORTS = 'SPORTS',
    CITIZEN = 'CITIZEN',
    LEGACY = 'LEGACY',
}

export enum CardSubType {
    RESIDENT = 'RESIDENT',
    NONRESIDENT = 'NONRESIDENT',
    MAIN = 'MAIN',
    OTHER = 'OTHER',
}

export enum CardActionType {
    SUBSCRIBE = 'SUBSCRIBE',
    LINK = 'LINK',
}

export enum CardStatus {
    PROCESSING = 'PROCESSING',
    ACTIVE = 'ACTIVE',
    REFUSED = 'REFUSED',
    DELETED = 'DELETED',
    EXPIRED = 'EXPIRED',
    INACTIVE = 'INACTIVE', // Front-end only
    ACTIVATING = 'ACTIVATING',
}

export enum BardCodeType {
    BARCODE = 'barcode',
    QRCODE = 'qrcode',
}

export interface IAddress {
    postCode: string;
    number: number;
    street: string;
    city: string;
    province: string;
    latitude: number;
    longitude: number;
    streetName: string;
    type: string;
    direction: string;
    unit: string;
}

export interface FormFile {
    url: string;
    file: File;
}

// Frontend type management
export interface CardSelectorFE {
    label: string;
    cardType: CardType;
    status: CardStatus;
}

export interface UserCardFE extends UserCard {
    renewalAlertDisplay?: boolean;
    subscriptions?: GetUserActiveSubscriptionsResponse[];
}

export interface CardConfigDataFE {
    org: Organisation;
    config: OrganisationConfiguration;
}

export enum CardAction {
    SUBSCRIBE = 'SUBSCRIBE',
    RENEW = 'RENEW',
    LINK = 'LINK',
}

export interface CardActionPayload {
    action: CardAction;
    card: Partial<UserCardFE>;
}

export interface FEWallet {
    [key: string]: Partial<UserCardFE>;
}

export interface UserOrgSubscription {
    _id: string;
    order: number;
    icon: string;
    code: string;
    name: string;
    description: string;
    cost: number;
    taxable: boolean;
    cardType: string;
    conditions: string[];
    status: UserOrgSubscriptionStatus;
    messages: UserOrgSubscriptionMessage[];
}

export interface UserOrgActiveSubscription extends UserOrgSubscription {
    subscriptionDate: string;
    expirationDate: string;
}

export interface UserOrgInactiveSubscription extends UserOrgSubscription {
    durationMonth: number;
    fixedSubscriptionDates: boolean;
    fixedStartDate: string;
    fixedEndDate: string;
}

export enum UserOrgSubscriptionStatus {
    AVAILABLE = 'AVAILABLE',
    UNAVAILABLE = 'UNAVAILABLE',
    ACTIVE = 'ACTIVE',
}

export enum UserOrgSubscriptionMessageCategory {
    CARD_EXP = 'CARD_EXP',
    CARD_TYPE = 'CARD_TYPE',
    MIN_AGE = 'MIN_AGE',
    MAX_AGE = 'MAX_AGE',
    MIN_AGE_BY_DATE = 'MIN_AGE_BY_DATE',
    MAX_AGE_BY_DATE = 'MAX_AGE_BY_DATE',
    RESIDENT = 'RESIDENT',
    NONRESIDENT = 'NONRESIDENT',
    PARENT = 'PARENT',
}

export enum UserOrgSubscriptionMessageType {
    WARNING = 'WARNING',
    ERROR = 'ERROR',
}

export interface UserOrgSubscriptionMessage {
    categ: UserOrgSubscriptionMessageCategory;
    type: UserOrgSubscriptionMessageType;
    message: string;
}

export type GetUserSubscriptionsResponse = (UserOrgActiveSubscription | UserOrgInactiveSubscription)[];

export interface FESubscription {
    _id: string;
    order: number;
    icon: string;
    code: string;
    name: string;
    description: string;
    cardType: string;
    taxable: boolean;
    cost: number;
    durationMonth?: number;
    renewalDelay: number;
    startDate?: string;
    endDate?: string;
    conditions?: string;
}

export interface GetUserActiveSubscriptionsResponse {
    cardType: string;
    code: string;
    cost: number;
    description: string;
    isRenewable: boolean;
    endDate: string;
    purposeStartDate: string;
    purposeEndDate: string;
    category: SubscriptionCategory;
    subscriptionType: SubscriptionType;
    icon: string;
    messages: string[];
    name: string;
    startDate: string;
    status: string;
    taxable: boolean;
    _id?: string;
}

export enum FamiliyLink {
    CHILD = 'CHILD',
    PARENT = 'PARENT',
    OTHER = 'OTHER',
    SPOUSE = 'SPOUSE',
}

export interface FamiliyNameLink {
    firstName: string;
    lastName: string;
    sex: Sex;
    birthDate: Date;
}

export interface PostFamilyLink {
    sourceLink: FamiliyLink;
    targetLink: FamiliyLink;
    proofOfIdentity?: string[];
    nameLinking?: FamiliyNameLink;
    email?: string;
    cardNumber?: string;
}

export enum LinkStatus {
    PENDING = 'PENDING',
    INVITED = 'INVITED',
}

export interface PostNewFamilyLink {
    picture?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    sourceLink: FamiliyLink;
    targetLink: FamiliyLink;
    proofOfIdentity?: string[];
    birthDate?: Date;
    address: LinkAddress;
    sex?: string;
}

/*export interface FamilyLinkDialogData {
    refreshFamilyLinks?: () => void;
    profile: Profile;
    orgConfig: OrganisationConfiguration;
    coreConfig: CoreConfig;
}*/
